import Glow from "@/components/core/ChallengeItem/Glow"
import useChallengeItem from "@/components/core/ChallengeItem/useChallengeItem"
import { BonusLockedRewardModal } from "@/components/pages/CartoonDetailPage/GameSection/BonusLockedRewardModal"
import { LocationLockedRewardModal } from "@/components/pages/CartoonDetailPage/GameSection/LocationLockedRewardModal"
import Badge from "@/components/shared/Badge"
import Button from "@/components/shared/Button"
import Modal from "@/components/shared/Modal"
import ToolTip from "@/components/shared/ToolTip"
import Typography from "@/components/shared/Typography"
import { ROUTES } from "@/conf"
import Image from "next/image"
import Link from "next/link"
import { useTranslation } from "react-i18next"
import { twMerge } from "tailwind-merge"

export enum ChallengeType {
  GAME = "GAME",
  LOCATION = "LOCATION",
  BONUS = "BONUS",
}

export enum ChallengeStatus {
  COMPLETED = "COMPLETED",
  LOCKED = "LOCKED",
  ACTIVE = "ACTIVE",
}

type ChallengeItemProps = {
  type: ChallengeType
  groshak?: number
  status?: ChallengeStatus
  comicsSlug: string
}

const ChallengeItem = ({ type, groshak, status = ChallengeStatus.LOCKED, comicsSlug }: ChallengeItemProps) => {
  const {
    handleBonusClick,
    handleLocationClick,
    mainColor,
    shine,
    showBonusLockedModal,
    showLockedLocationRewardModal,
    textColor,
    title,
    locked,
    setShowLockedLocationRewardModal,
    setShowBonusLockedModal,
    isDesktop,
  } = useChallengeItem({ comicsSlug, status, type })

  const { t } = useTranslation("common")

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-primary-500 p-4 pt-6">
        {shine && <Glow mainColor={mainColor} />}
        {groshak !== 0 && status !== ChallengeStatus.COMPLETED && (
          <Badge
            className="absolute left-1/2 top-10 -translate-x-1/2 lg:left-2 lg:top-2 lg:translate-x-0"
            variant="reward"
            inverse
            glow
            icon={
              <div className="relative flex h-5 w-5">
                <Image src="/assets/images/grosak.svg" className="!h-5 !w-5" alt="" fill />
              </div>
            }
          >
            {groshak}
          </Badge>
        )}

        <div className="flex h-full flex-col items-center justify-center">
          <div className="flex justify-center">
            <Image
              className="pb-2.5"
              src={`/assets/images/challenge_${type}${status === ChallengeStatus.COMPLETED ? "_active" : ""}.svg`}
              width={66}
              height={84}
              alt=""
              priority
            />
          </div>
          <Typography
            type={isDesktop ? "l" : "caption"}
            className={twMerge("text-center font-medium", shine && textColor)}
          >
            {title}
          </Typography>

          {type === ChallengeType.GAME && (
            <Link href={`${ROUTES.cartoonDetail}/${comicsSlug}?section=game`} className="mt-[18px] w-full lg:mt-7">
              <Button color="primary" fullWidth size={isDesktop ? "md" : "sm"} className="text-[10px] sm:text-[14px]">
                {isDesktop ? t("comicsDetail.playComics") : t("comicsDetail.playComicsShort")}
              </Button>
            </Link>
          )}

          {type === ChallengeType.LOCATION && (
            <div className="mt-[18px] flex w-full items-center justify-center lg:mt-7">
              {locked ? (
                <Button onClick={handleLocationClick} color="gray" fullWidth size={isDesktop ? "md" : "sm"}>
                  <div className="h-6 w-6">
                    <Image src="/assets/images/lock.svg" width={24} height={24} alt={ChallengeType.LOCATION} />
                  </div>
                </Button>
              ) : (
                <Badge variant="darkGreen" className="text-[10px] sm:text-[14px]">
                  {t("comicsDetail.finished")}
                </Badge>
              )}
            </div>
          )}

          {type === ChallengeType.BONUS && (
            <div className="mt-[18px] w-full lg:mt-7">
              {locked ? (
                <ToolTip text="Jsi na místě? Vyřeš bonusovou otázku a odměna je tvá.">
                  <Button onClick={handleBonusClick} color="gray" fullWidth size={isDesktop ? "md" : "sm"}>
                    <div className="h-6 w-6">
                      <Image src="/assets/images/lock.svg" width={24} height={24} alt={ChallengeType.BONUS} />
                    </div>
                  </Button>
                </ToolTip>
              ) : (
                <Button
                  onClick={handleBonusClick}
                  color={status === ChallengeStatus.COMPLETED ? "green" : "primary"}
                  fullWidth
                  size={isDesktop ? "md" : "sm"}
                  className="text-[10px] sm:text-[14px]"
                >
                  {status === ChallengeStatus.COMPLETED ? "Dokončeno" : "Spustit"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal isVisible={showLockedLocationRewardModal} setIsVisible={setShowLockedLocationRewardModal}>
        <LocationLockedRewardModal
          closeModal={() => {
            setShowLockedLocationRewardModal(false)
          }}
        />
      </Modal>

      <Modal isVisible={showBonusLockedModal} setIsVisible={setShowBonusLockedModal}>
        <BonusLockedRewardModal />
      </Modal>
    </>
  )
}

export default ChallengeItem
